import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useAngularContext } from 'AngularContext';
import { useRequiredCurrentUser, useTargetBrandConfig } from 'FrontRoyalAngular';
import { getCohort, getProgramInclusion, userActionsApi } from 'Users';
import { getProgramConfigValue } from 'Program';
import { getSupportEmailAddress } from 'Cohorts';
import { isIncluded } from 'ProgramInclusion';
import { navigationHelper } from 'NavigationHelpers';
import { type SignOutHelperType } from 'Authentication';
import { Button } from 'Button';
import { InlineLink } from 'InlineLink';
import { Modal } from 'Modal';
import { Text } from 'Text';
import { showToast } from 'Toast';

// Delay sign-out long enough for the user to read the success toast (matches the old directive).
const SIGN_OUT_DELAY_MS = 1000;

// Confirm dialog for the irreversible account deletion. `onConfirm` runs the deletion; the modal
// closes first (matching the old DialogModal.confirm behavior of dismissing on confirm).
function ConfirmDeletionModal({ onConfirm, onClose }: { onConfirm: () => void; onClose: () => void }) {
    const { t } = useTranslation('back_royal');

    return (
        <Modal
            titleKey="settings.edit_account.confirm_account_deletion_title"
            size="md"
            hideCloseButton
            onClose={onClose}
        >
            {({ closeModal }) => (
                <div className="flex flex-col gap-6 px-7.5 pb-7.5">
                    <Text as="p">{t('settings.edit_account.confirm_account_deletion')}</Text>
                    <div className="flex justify-end gap-3">
                        <Button variant="bordered" color="slate-grey" height={9} onPress={closeModal}>
                            {t('settings.edit_account.cancel')}
                        </Button>
                        <Button
                            color="red"
                            height={9}
                            onPress={() => {
                                closeModal();
                                onConfirm();
                            }}
                        >
                            {t('settings.edit_account.delete')}
                        </Button>
                    </div>
                </div>
            )}
        </Modal>
    );
}

// Account deletion. Self-serve users get a delete button gated behind a confirm dialog; everyone
// else sees defer/contact-support copy. Deletion is irreversible, so the confirm dialog is required.
export function DeleteAccountSection() {
    const { t } = useTranslation('back_royal');
    const $injector = useAngularContext();
    const currentUser = useRequiredCurrentUser();
    const brandConfig = useTargetBrandConfig();
    const { loadFaqUrl } = navigationHelper();
    const [deleteAccount] = userActionsApi.useDeleteAccountMutation();
    const [confirmingDeletion, setConfirmingDeletion] = useState(false);

    const brandNameShort = brandConfig.brandNameShort;

    // Defer info is only relevant for included students in a program that supports a schedule.
    const cohort = getCohort(currentUser);
    const showDeferInfo =
        isIncluded(getProgramInclusion(currentUser)) &&
        !!cohort &&
        !!getProgramConfigValue<boolean>(cohort.programType, 'supportsSchedule');
    const deferSupportEmail = cohort ? (getSupportEmailAddress(cohort, 'support') ?? '') : '';

    // Schedules the (irreversible) deletion, surfaces the toast, then signs the user out after a beat
    // so they can read it. On failure we leave the user on the page (matches the old directive).
    const performDeletion = async () => {
        const signOutHelper = $injector?.get<SignOutHelperType>('SignOutHelper');
        try {
            await deleteAccount().unwrap();
            showToast({ message: t('settings.edit_account.account_scheduled_for_deletion'), type: 'success' });
            setTimeout(() => signOutHelper?.signOut(), SIGN_OUT_DELAY_MS);
        } catch {
            // Deletion failed — leave the user on the page.
        }
    };

    return (
        <div className="flex flex-col gap-3 py-6">
            <Text as="h2" color="black" weight="semibold" className="text-base">
                {t('settings.edit_account.delete_account')}
            </Text>

            {currentUser.canDeleteOwnAccount ? (
                <div className="flex flex-col items-start gap-3">
                    <Text as="p" variant="small">
                        {t('settings.edit_account.delete_account_description')}
                    </Text>
                    <Button color="red" height={9} onPress={() => setConfirmingDeletion(true)}>
                        {t('settings.edit_account.delete_account')}
                    </Button>
                </div>
            ) : (
                <>
                    {showDeferInfo ? (
                        <Text as="p" variant="small">
                            <Trans
                                t={t}
                                i18nKey="settings.edit_account.defer_instead"
                                values={{ brandName: brandNameShort, brandEmail: deferSupportEmail }}
                                components={{
                                    mailto: (
                                        <InlineLink
                                            className="text-xs"
                                            href={`mailto:${deferSupportEmail}?subject=Deferral%20Request`}
                                        />
                                    ),
                                    faq: (
                                        <InlineLink
                                            className="text-xs"
                                            onPress={() => loadFaqUrl('/help/what-does-it-mean-to-defer', '_blank')}
                                        />
                                    ),
                                }}
                            />
                        </Text>
                    ) : null}
                    <Text as="p" variant="small">
                        <Trans
                            t={t}
                            i18nKey="settings.edit_account.should_you_wish_to_delete"
                            values={{
                                brandName: brandNameShort,
                                brandEmail: brandConfig.emailAddressForUsername('support'),
                            }}
                            components={{
                                mailto: (
                                    <InlineLink
                                        className="text-xs"
                                        href={`mailto:${brandConfig.emailAddressForUsername(
                                            'support',
                                        )}?subject=Account%20Deletion%20Request`}
                                    />
                                ),
                                strong: <strong />,
                            }}
                        />
                    </Text>
                </>
            )}

            {confirmingDeletion ? (
                <ConfirmDeletionModal onConfirm={performDeletion} onClose={() => setConfirmingDeletion(false)} />
            ) : null}
        </div>
    );
}
